import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  EasyCheckButton,
  EasyCheckTextField,
  EasyCheckDialog
} from '../../common/Controls';
import { Straighten as SizeIcon } from '@mui/icons-material';

const DPI_OPTIONS = [75, 150, 200, 300, 600];

const calcDimensions = (pixelWidth, pixelHeight, dpi) => ({
  width: parseFloat(((pixelWidth / dpi) * 2.54).toFixed(2)),
  height: parseFloat(((pixelHeight / dpi) * 2.54).toFixed(2)),
});

const findBestDpi = (pixelWidth) => {
  // Pick the DPI where calculated width falls in typical cheque range (14-22 cm)
  const matches = DPI_OPTIONS.filter((dpi) => {
    const w = (pixelWidth / dpi) * 2.54;
    return w >= 14 && w <= 22;
  });
  // Prefer 300 if it matches, otherwise first match, otherwise default 300
  if (matches.includes(300)) return 300;
  return matches[0] || 300;
};

const DpiInputDialog = ({ open, onClose, onConfirm, pixelWidth, pixelHeight, initialWidth, initialHeight, hasDpiMetadata }) => {
  const [selectedDpi, setSelectedDpi] = useState('300');
  const [widthCm, setWidthCm] = useState('17.5');
  const [heightCm, setHeightCm] = useState('7.5');

  const hasPixels = pixelWidth > 0 && pixelHeight > 0;

  // Determine the best default DPI based on pixel dimensions
  const bestDpi = useMemo(() => {
    if (!hasPixels) return 300;
    return findBestDpi(pixelWidth);
  }, [pixelWidth, hasPixels]);

  useEffect(() => {
    if (!open) return;

    // DPI metadata is only trusted when it yields a plausible cheque width —
    // downsized images often keep the scanner's original DPI tag (e.g. a 661px
    // image tagged 300dpi computes to 5.6cm), which would break printing.
    const metadataPlausible =
      hasDpiMetadata && initialWidth && initialHeight &&
      initialWidth >= 12 && initialWidth <= 30;

    if (metadataPlausible) {
      setSelectedDpi('custom');
      setWidthCm(String(initialWidth));
      setHeightCm(String(initialHeight));
    } else if (hasPixels) {
      // No reliable DPI metadata — auto-select best DPI
      const dpi = bestDpi;
      setSelectedDpi(String(dpi));
      const dims = calcDimensions(pixelWidth, pixelHeight, dpi);
      setWidthCm(String(dims.width));
      setHeightCm(String(dims.height));
    } else {
      setSelectedDpi('300');
      setWidthCm(String(initialWidth || 17.5));
      setHeightCm(String(initialHeight || 7.5));
    }
  }, [open, hasDpiMetadata, initialWidth, initialHeight, hasPixels, pixelWidth, pixelHeight, bestDpi]);

  const handleDpiChange = (_e, newValue) => {
    if (newValue === null) return; // MUI ToggleButtonGroup sends null on deselect
    setSelectedDpi(newValue);
    if (newValue !== 'custom' && hasPixels) {
      const dpi = parseInt(newValue, 10);
      const dims = calcDimensions(pixelWidth, pixelHeight, dpi);
      setWidthCm(String(dims.width));
      setHeightCm(String(dims.height));
    }
  };

  const handleConfirm = () => {
    const w = parseFloat(widthCm);
    const h = parseFloat(heightCm);
    if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return;
    onConfirm(w, h);
  };

  const isCustom = selectedDpi === 'custom';

  return (
    <EasyCheckDialog
      open={open}
      onClose={onClose}
      title="Confirm Cheque Dimensions"
      titleIcon={<SizeIcon />}
      variant="form"
      maxWidth="xs"
      actions={
        <>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={onClose}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton buttonType="form" onClick={handleConfirm}>
            Confirm
          </EasyCheckButton>
        </>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Please confirm the physical dimensions of the cheque to ensure accurate
          field positioning.
        </Typography>

        {hasPixels && (
          <Typography variant="caption" color="text.secondary">
            Image resolution: {pixelWidth} x {pixelHeight} pixels
          </Typography>
        )}

        {/* DPI Selector */}
        {hasPixels && (
          <Box>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
              Scanner DPI
            </Typography>
            <ToggleButtonGroup
              value={selectedDpi}
              exclusive
              onChange={handleDpiChange}
              size="small"
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  py: 0.75,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              {DPI_OPTIONS.map((dpi) => (
                <ToggleButton key={dpi} value={String(dpi)}>
                  {dpi}
                </ToggleButton>
              ))}
              <ToggleButton value="custom">
                Custom
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <EasyCheckTextField
            variant="form"
            label="Width (cm)"
            type="number"
            value={widthCm}
            onChange={(e) => {
              setWidthCm(e.target.value);
              if (!isCustom) setSelectedDpi('custom');
            }}
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
            }}
            inputProps={{ step: '0.1', min: '1' }}
            fullWidth
          />
          <EasyCheckTextField
            variant="form"
            label="Height (cm)"
            type="number"
            value={heightCm}
            onChange={(e) => {
              setHeightCm(e.target.value);
              if (!isCustom) setSelectedDpi('custom');
            }}
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
            }}
            inputProps={{ step: '0.1', min: '1' }}
            fullWidth
          />
        </Box>

        {/* Helper text showing calculation */}
        {hasPixels && !isCustom && (
          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
            At {selectedDpi} DPI: {widthCm} x {heightCm} cm
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Standard cheque size is approximately 17.5 cm x 7.5 cm
        </Typography>
      </Stack>
    </EasyCheckDialog>
  );
};

export default DpiInputDialog;
